// ---------------------------------------------------------------------------
// T141-1 — **THE HOUSE'S OWN SEAT. WHO 12TH HOUSE SUPPORT IS, IN ONE DECLARATION.**
//
// Her SECOND ruling on this: the support desk gets its own seat, its own name in every participant
// list, block rule and picker, all desk replies authored as IT, and **her personal seat out of
// support entirely.** T141-1's own build note is what she is answering — that round marked the
// agent's answer with a sentence because the bubble carried HER name and HER photograph, and said in
// as many words that making it read as somebody else means a support seat and needs her word IN
// ADVANCE. She has now given it twice.
//
// **IT IS A `.js` LAW THAT IMPORTS NOTHING**, which is the entry requirement that lets a `.mjs` gate
// LOAD and RUN every clause of it (R146: reach is transitive), and it is SHARED because the SERVER
// resolves the seat and the CLIENT renders its name — two spellings of *who the house is* is two
// answers on the one surface a member cannot tell apart from a person (R28/R29).
// ---------------------------------------------------------------------------

/**
 * The seat's address. It is a real address on her own domain rather than a `.invalid` one, and that
 * is a measurement rather than a preference: `identityFrom` derives its FIXTURE BADGE from the
 * `@harness.invalid` convention (R144), so a `.invalid` support seat would wear a harness badge in
 * her own queue on every answer the desk ever sent.
 *
 * **NOBODY SIGNS IN AS IT, AND THAT IS ENFORCED AT THE SESSION MINT RATHER THAN LEFT TO LUCK.**
 * `handleAuthLogin` takes an ADDRESS and mints a seven-day session, so a house seat with a
 * guessable address is the house's own voice in the Living Room handed to whoever types it.
 * `createSignedSession` refuses this address and `readSignedSession` refuses it again — one law,
 * asked at both ends, which is REV-1's own shape on the one door where the cost is the brand.
 */
export const SUPPORT_SEAT_EMAIL = "support@12thhouse.ai";

/**
 * >>> **THE RENDERED NAME IS A CONSTANT IN SOURCE AND NEVER A ROW VALUE, AND THAT ONE FACT IS WHAT
 * KEEPS LR1 RULING 3 WHOLE. <<<**
 *
 * Ruling 3 is that the only name any surface renders is the ROOM FIRST-NAME — `roomFirstName` takes
 * the first token precisely so a surname can never reach a screen. Measured before a line was
 * written: a seat named *12th House Support* renders **@12th** under that law, which is the defect
 * this round would have shipped by naming the seat naively.
 *
 * So the house seat's name bypasses `roomFirstName` — and it is safe to bypass **only because it is
 * declared here**. No member-supplied string can reach a full-name render: the row's own `name`,
 * `first_name` and `living_room_name` are never read for this seat. Every real member is untouched.
 */
export const SUPPORT_SEAT_NAME = "12th House Support";

/** Fold an address the way every roster in this house folds one. */
function fold(email) {
  return String(email ?? "").trim().toLowerCase();
}

/**
 * **IS THIS ADDRESS THE HOUSE'S OWN SEAT.** One reader, so the identity, the block rule, the message
 * gate, the session doors and the migration cannot hold five opinions about it.
 *
 * It answers on a real non-empty string and on nothing else — presence, never coercion (R53/R136/
 * R137) — because it arrives off a database row and off a request body alike, and `Boolean(true)`
 * folding to `"true"` must never resolve to the house.
 */
export function isHouseSeat(email) {
  if (typeof email !== "string") return false;
  const at = fold(email);
  return Boolean(at) && at === SUPPORT_SEAT_EMAIL;
}

/**
 * >>> **THE HOUSE SEAT IS NOT AN OWNER, AND THAT IS THE ROUND'S ONE REAL SECURITY DECISION. <<<**
 *
 * The two reaches it genuinely needs are ITEM 3's: it may not be BLOCKED (a member blocking the
 * support desk removes herself from the one place she can be helped from, silently, with nobody
 * told) and it may write to somebody who does not follow it (the desk answers a member who has never
 * followed anybody).
 *
 * **Both of those read the `owner` boolean today — and `OWNER_EMAILS` also carries the dashboard,
 * the refund door and the grant door.** Putting a seat whose address is written in source onto that
 * roster hands all three to anybody who reaches it. So `house` is its own fact (R18) and confers
 * NEITHER: no dashboard, no refunds, no delete-anything, no moderation queue.
 */
export function houseReach(who) {
  return Boolean(who?.owner) || Boolean(who?.house);
}
